import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
    IconCirclePlus,
    IconCircleMinus,
    IconPencil,
    IconSend,
    IconTrash,
    IconX,
} from "@tabler/icons-react";

import { Button, Input, Text } from "@fragments";

import "./styles.scss";

const ADD_MAX_POINTS = 10000;

function PointsActionCard({
    icon,
    title,
    inputLabel,
    submitLabel,
    helperText,
    initialPoints,
    minPoints,
    maxPoints,
    loading,
    onSubmit,
    onClose,
}) {
    const [points, setPoints] = useState(initialPoints);

    async function handleSubmit() {
        const success = await onSubmit(points);

        if (success) {
            setPoints(initialPoints);
        }
    }

    return (
        <div className="admin-post-manage__expanded">
            <div className="admin-post-manage__expanded-header">
                <Text.h5 left={icon} contrast={8}>
                    {title}
                </Text.h5>
                <Button
                    icon={<IconX />}
                    variant="text"
                    color="inverse"
                    size="small"
                    preventLoading
                    onClick={onClose}
                />
            </div>

            <Input.Integer
                disabled={loading}
                value={points}
                onInput={setPoints}
                min={minPoints}
                max={maxPoints}
                label={inputLabel}
                fullWidth
                autoFocus
            />

            <Text.span contrast={5}>{helperText}</Text.span>

            <Button
                loading={loading}
                size="small"
                variant="default"
                color="inverse"
                onClick={handleSubmit}
                fullWidth
            >
                {submitLabel}
            </Button>
        </div>
    );
}

export default function AdminPostManageView({
    post,
    loading,
    canManageSchedule = false,
    onEdit,
    onPostNow,
    onAddPoints,
    onDeducePoints,
    onDeletePost,
}) {
    const { t } = useTranslation("groupModals");
    const [addPointsOpen, setAddPointsOpen] = useState(false);
    const [deducePointsOpen, setDeducePointsOpen] = useState(false);

    const currentPoints = post?.insights?.points || 0;

    async function handleAddSubmit(points) {
        const success = await onAddPoints(points);

        if (success) {
            setAddPointsOpen(false);
        }

        return success;
    }

    async function handleDeduceSubmit(points) {
        const success = await onDeducePoints(points);

        if (success) {
            setDeducePointsOpen(false);
        }

        return success;
    }

    return (
        <div className="admin-post-manage px-3 pt-3">
            {canManageSchedule && (
                <>
                    <Button
                        className="admin-post-manage__action-btn"
                        variant="text"
                        color="overlay"
                        fullWidth
                        preventLoading
                        disabled={loading}
                        onClick={onEdit}
                        startIcon={<IconPencil />}
                    >
                        {t("adminPostManage.edit.title")}
                    </Button>

                    <Button
                        className="admin-post-manage__action-btn"
                        variant="text"
                        color="overlay"
                        fullWidth
                        preventLoading
                        disabled={loading}
                        onClick={onPostNow}
                        startIcon={<IconSend />}
                    >
                        {t("adminPostManage.postNow.title")}
                    </Button>

                    <hr className="admin-post-manage__divider" />
                </>
            )}

            {addPointsOpen ? (
                <PointsActionCard
                    icon={<IconCirclePlus />}
                    title={t("adminPostManage.addPoints.title")}
                    inputLabel={t("adminPostManage.addPoints.inputLabel")}
                    submitLabel={t("adminPostManage.addPoints.submit")}
                    helperText={t("adminPostManage.addPoints.helper", {
                        max: ADD_MAX_POINTS,
                    })}
                    initialPoints={10}
                    minPoints={1}
                    maxPoints={ADD_MAX_POINTS}
                    loading={loading}
                    onSubmit={handleAddSubmit}
                    onClose={() => setAddPointsOpen(false)}
                />
            ) : (
                <Button
                    className="admin-post-manage__action-btn"
                    variant="text"
                    color="overlay"
                    fullWidth
                    preventLoading
                    onClick={() => setAddPointsOpen(true)}
                    startIcon={<IconCirclePlus />}
                >
                    {t("adminPostManage.addPoints.title")}
                </Button>
            )}

            {deducePointsOpen ? (
                <PointsActionCard
                    icon={<IconCircleMinus />}
                    title={t("adminPostManage.deducePoints.title")}
                    inputLabel={t("adminPostManage.deducePoints.inputLabel")}
                    submitLabel={t("adminPostManage.deducePoints.submit")}
                    helperText={t("adminPostManage.deducePoints.helper", {
                        max: currentPoints,
                    })}
                    initialPoints={1}
                    minPoints={1}
                    maxPoints={currentPoints}
                    loading={loading}
                    onSubmit={handleDeduceSubmit}
                    onClose={() => setDeducePointsOpen(false)}
                />
            ) : (
                <Button
                    className="admin-post-manage__action-btn"
                    variant="text"
                    color="overlay"
                    fullWidth
                    preventLoading
                    disabled={!currentPoints}
                    onClick={() => setDeducePointsOpen(true)}
                    startIcon={<IconCircleMinus />}
                >
                    {t("adminPostManage.deducePoints.button", {
                        points: currentPoints,
                    })}
                </Button>
            )}

            <hr className="admin-post-manage__divider" />

            <Button
                className="admin-post-manage__action-btn"
                variant="text"
                color="error"
                fullWidth
                preventLoading
                onClick={onDeletePost}
                startIcon={<IconTrash />}
            >
                {t("adminPostManage.deletePost.title")}
            </Button>
        </div>
    );
}
